import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export interface AppToastDetail {
  title: string;
  message?: string;
  tone?: 'info' | 'success' | 'warning' | 'error';
}

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatConfidence(value: number | null): string {
  if (value === null) return '-';
  return `${Math.round(value * 100)}%`;
}

export function confidenceTone(value: number | null): 'high' | 'medium' | 'low' {
  if (value === null) return 'low';
  if (value >= 0.85) return 'high';
  if (value >= 0.7) return 'medium';
  return 'low';
}

export function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const seconds = Math.round((now - then) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

const dateTimeFormatters = new Map<string, Intl.DateTimeFormat>();

export function formatDateTime(iso: string, timeZone?: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;

  const formatterKey = timeZone ?? 'browser-local';
  let formatter = dateTimeFormatters.get(formatterKey);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
      ...(timeZone ? { timeZone } : {}),
    });
    dateTimeFormatters.set(formatterKey, formatter);
  }

  return formatter.format(date);
}

export function getQueueReceivedOn(input: {
  emailReceivedAt?: string | null;
  receivedAt: string;
}): string {
  const emailReceivedAt = input.emailReceivedAt?.trim();
  return emailReceivedAt || input.receivedAt;
}

export type QueueGroupingField = 'email' | 'customer' | 'po' | 'salesOrder';

export function getQueueGroupValue(
  input: {
    customerName?: string | null;
    customerNumber?: string | null;
    emailReceivedAt?: string | null;
    emailSender?: string | null;
    emailSubject?: string | null;
    poNumber?: string | null;
    receivedAt: string;
    salesOrderNumber?: string | null;
  },
  field: QueueGroupingField,
): string {
  switch (field) {
    case 'email': {
      const sender = input.emailSender?.trim() || 'Unknown sender';
      const subject = input.emailSubject?.trim() || 'No subject';
      const receivedOn = getQueueReceivedOn(input);
      return `${sender} | ${subject} | ${receivedOn}`;
    }
    case 'customer': {
      const customer = input.customerName?.trim() || 'Unknown customer';
      const number = input.customerNumber?.trim();
      return number ? `${customer} (${number})` : customer;
    }
    case 'salesOrder':
      return input.salesOrderNumber?.trim() || 'Pending sales order';
    case 'po':
      return input.poNumber?.trim() || 'Pending PO';
  }
}

export function formatQueueGroupLabel(
  input: {
    customerName?: string | null;
    customerNumber?: string | null;
    emailReceivedAt?: string | null;
    emailSender?: string | null;
    emailSubject?: string | null;
    poNumber?: string | null;
    receivedAt: string;
    salesOrderNumber?: string | null;
  },
  field: QueueGroupingField,
): string {
  switch (field) {
    case 'email': {
      const sender = input.emailSender?.trim() || 'Unknown sender';
      const subject = input.emailSubject?.trim() || 'No subject';
      const receivedOn = getQueueReceivedOn(input);
      return `${sender} - ${subject} - Received ${receivedOn}`;
    }
    case 'customer':
      return getQueueGroupValue(input, field);
    case 'salesOrder':
      return input.salesOrderNumber?.trim() || 'Pending sales order';
    case 'po':
      return input.poNumber?.trim() || 'Pending PO';
  }
}

export function showAppToast(detail: AppToastDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<AppToastDetail>('app:toast', { detail }));
}

export function getQueueItemReference(input: {
  emailSubject?: string | null;
  uploadedFileName?: string | null;
  id?: string | null;
}): string {
  const uploadedFileName = input.uploadedFileName?.trim();
  if (uploadedFileName) return uploadedFileName;
  const id = input.id?.trim();
  if (id) return id;
  const emailSubject = input.emailSubject?.trim();
  if (emailSubject) return emailSubject;
  return input.id?.trim() || 'Untitled queue item';
}

export function getQueueItemSecondaryReference(input: {
  emailSubject?: string | null;
  uploadedFileName?: string | null;
}): string | null {
  const emailSubject = input.emailSubject?.trim();
  const uploadedFileName = input.uploadedFileName?.trim();
  if (emailSubject && uploadedFileName) return uploadedFileName;
  if (uploadedFileName) return null;
  return null;
}

export function formatWorkflowStateLabel(state: string): string {
  switch (state) {
    case 'Retries Exhausted':
      return 'Needs operator review';
    case 'Waiting for UoM Mapping':
      return 'Waiting for units mapping';
    case 'UoM Mapped':
      return 'Units mapped';
    case 'UoM Mapping Failed':
      return 'Units mapping failed';
    default:
      return state;
  }
}

export function formatPipelineStepLabel(value: string | null | undefined): string {
  if (!value) return 'Step';
  switch (value) {
    case 'ingestion':
      return 'Ingestion';
    case 'ocr':
    case 'document_intelligence':
      return 'OCR';
    case 'low_confidence_recovery':
      return 'Low-confidence recovery';
    case 'low_confidence_adjudication':
      return 'Low-confidence review';
    case 'classification':
    case 'document_classification':
      return 'Classification';
    case 'duplicate':
    case 'duplicate_check':
      return 'Duplicate check';
    case 'matching':
      return 'Matching';
    case 'matching_fanout':
      return 'Matching parameters';
    case 'draft_rejected':
      return 'Draft rejected';
    case 'draft_generation_error':
      return 'Draft generation error';
    case 'legal_entity_matching':
      return 'Legal entity matching';
    case 'site_matching':
      return 'Site matching';
    case 'customer_matching':
      return 'Customer matching';
    case 'product_matching':
      return 'Product matching';
    case 'uom_mapping':
      return 'Units mapping';
    case 'payment_terms_mapping':
      return 'Payment terms mapping';
    case 'delivery_terms_mapping':
      return 'Delivery terms mapping';
    case 'delivery_mode_mapping':
      return 'Delivery mode mapping';
    case 'validation':
    case 'angie_checklist':
      return 'Order checks';
    case 'draft':
    case 'draft_creation':
      return 'Draft creation';
    case 'confirm':
    case 'confirming':
      return 'Confirmation';
    case 'approval':
      return 'Approval';
    case 'acknowledgement':
      return 'Acknowledgement';
    case 'resolved':
      return 'Resolved';
    default:
      return value
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
}
